// src/pages/AdminEvents.jsx
import React, { useEffect, useState } from "react";
import axios, { API_BASE } from "../services/api";
import { useNavigate } from "react-router-dom";

export default function AdminEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [editingPrices, setEditingPrices] = useState(null); // eventId | null
  const [selectedEvent, setSelectedEvent] = useState(null); // eventId | null
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [vipInv, setVipInv] = useState({}); // { [eventId]: { remainingVipTickets, reservedVipTickets, totalVipTickets } }
  const [holdQty, setHoldQty] = useState({});
  const [relQty, setRelQty] = useState({});

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshVipSummary(eventId) {
    try {
      const { data } = await axios.get(`/api/admin/events/${eventId}/vip/summary`);
      setVipInv((m) => ({ ...m, [eventId]: data }));
    } catch { }
  }

  const fetchEvents = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/events/list");
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const imgSrc = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${API_BASE}${url}`;
  };

  const isPastEvent = (event) => {
    const now = new Date();
    // startTime may be "HH:mm" or full timestamp; normalize
    const raw = (event.startTime || "").length === 5 ? `${event.startTime}:00` : event.startTime;
    const start = new Date(`${event.date}T${raw || "00:00:00"}`);
    return start < now;
  };

  const handleCancel = async (eventId) => {
    if (!window.confirm("Are you sure you want to cancel this event?")) return;
    try {
      await axios.put(`/api/admin/events/${eventId}/cancel`);
      await fetchEvents();
    } catch {
      alert("Failed to cancel event.");
    }
  };

  const handlePriceUpdate = async (eventId, standardPrice, vipPrice) => {
    try {
      await axios.put(`/api/admin/events/${eventId}/prices`, {
        standardTicketPrice: Number(standardPrice || 0),
        vipTicketPrice: Number(vipPrice || 0),
      });
      setEditingPrices(null);
      await fetchEvents();
    } catch {
      alert("Failed to update prices.");
    }
  };

  const handleViewStats = async (eventId) => {
    try {
      setSelectedEvent(eventId);
      setStats(null);
      const res = await axios.get(`/api/admin/events/${eventId}/stats`);
      setStats(res.data);
    } catch {
      alert("Failed to fetch stats.");
    }
  };

  // Publishing now happens from the Edit page (where validation is enforced)
  const goEditForPublish = (eventId) => {
    navigate(`/admin/events/${eventId}/edit?publish=1`);
  };

  return (
    <div className="page space-y-6">
      {/* Header */}
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Manage Events</h1>
            <p className="mt-1 text-slate-300">
              Edit prices, cancel events, and view sales performance.
            </p>
          </div>
          <span className="badge bg-brand-600/20 text-slate-100">
            {events.length} event{events.length === 1 ? "" : "s"}
          </span>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {error}
          </div>
        )}
      </section>

      {/* Loading skeleton */}
      {loading && (
        <section className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="grid gap-4 md:grid-cols-[180px_1fr]">
                <div className="aspect-[4/3] w-full rounded-xl bg-white/5" />
                <div className="space-y-2">
                  <div className="h-6 w-1/2 bg-white/5 rounded" />
                  <div className="h-4 w-1/3 bg-white/5 rounded" />
                  <div className="h-4 w-2/3 bg-white/5 rounded" />
                  <div className="h-8 w-40 bg-white/5 rounded mt-3" />
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* List */}
      {!loading && events.length === 0 && (
        <section className="card text-slate-300">No events yet.</section>
      )}

      {!loading && events.length > 0 && (
        <section className="grid gap-4">
          {events.map((ev) => {
            const past = isPastEvent(ev);
            const showEdit = !past && !ev.cancelled;

            // Some APIs use different property names; fall back sensibly
            const stdPrice = ev.standardPrice ?? ev.standardTicketPrice ?? 0;
            const vipPrice = ev.vipPrice ?? ev.vipTicketPrice ?? 0;

            return (
              <article key={ev.id} className="card">
                <div className="grid gap-4 md:grid-cols-[200px_1fr]">
                  {/* Artwork */}
                  <div className="relative">
                    <div
                      className="aspect-[4/3] w-full overflow-hidden rounded-2xl
                                    bg-white/[0.03] ring-1 ring-white/10 shadow-card"
                    >
                      {ev.imageUrl ? (
                        <img
                          src={imgSrc(ev.imageUrl)}
                          alt={ev.title}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="grid h-full place-items-center text-sm text-slate-400">
                          No image
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h2 className="text-xl font-semibold text-white truncate">
                        {ev.title}
                      </h2>

                      <div className="flex flex-wrap gap-2">
                        {ev.cancelled && (
                          <span className="badge bg-red-500/20 text-red-200">Cancelled</span>
                        )}
                        {ev.status === "PLANNED" && !ev.cancelled && (
                          <span className="badge bg-violet-500/20 text-violet-200">Coming soon</span>
                        )}
                        {past && !ev.cancelled && (
                          <span className="badge bg-white/10 text-slate-200">Past</span>
                        )}
                        {!past && !ev.cancelled && ev.status !== "PLANNED" && (
                          <span className="badge bg-emerald-500/20 text-emerald-200">
                            Upcoming
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-2 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
                      <div>
                        <span className="text-slate-400">Date</span>
                        <div className="text-white">{ev.date}</div>
                      </div>
                      {(ev.startTime || ev.endTime) && (
                        <div>
                          <span className="text-slate-400">Time</span>
                          <div className="text-white">
                            {ev.startTime} {ev.endTime ? `– ${ev.endTime}` : ""}
                          </div>
                        </div>
                      )}
                      {ev.location && (
                        <div className="sm:col-span-2">
                          <span className="text-slate-400">Location</span>
                          <div className="text-white">{ev.location}</div>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <Metric label="Standard Price" value={`£${Number(stdPrice).toFixed(2)}`} />
                      <Metric label="VIP Price" value={`£${Number(vipPrice).toFixed(2)}`} />
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button className="btn-secondary" onClick={() => handleViewStats(ev.id)}>
                        View Stats
                      </button>
                      {!past && !ev.cancelled && (
                        <button className="btn-secondary" onClick={() => refreshVipSummary(ev.id)}>
                          VIP Inventory
                        </button>
                      )}

                      {showEdit && (
                        <>
                          {ev.status === "PLANNED" && !ev.cancelled && (
                            <button
                              className="btn-primary"
                              onClick={() => goEditForPublish(ev.id)}
                            >
                              Publish (open ticketing)
                            </button>
                          )}

                          <button
                            className="btn-secondary"
                            onClick={() => navigate(`/admin/events/${ev.id}/edit`)}
                          >
                            Edit Details
                          </button>

                          <button
                            className="btn-secondary"
                            onClick={() => {
                              setEditingPrices(ev.id);
                              setSelectedEvent(null);
                              setStats(null);
                            }}
                          >
                            Edit Prices
                          </button>

                          <button
                            className="btn-secondary bg-red-600 text-white hover:bg-red-700"
                            onClick={() => handleCancel(ev.id)}
                          >
                            Cancel Event
                          </button>
                        </>
                      )}
                    </div>

                    {/* VIP inventory (admin-only quick controls) */}
                    {selectedEvent !== ev.id && vipInv[ev.id] && !past && !ev.cancelled && (
                      <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="badge bg-white/10 text-slate-200">
                            VIP Remaining: {vipInv[ev.id].remainingVipTickets}
                          </div>
                          <div className="badge bg-white/10 text-slate-200">
                            VIP Reserved: {vipInv[ev.id].reservedVipTickets}
                          </div>
                          <div className="badge bg-white/10 text-slate-200">
                            VIP Total: {vipInv[ev.id].totalVipTickets}
                          </div>
                        </div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min={1}
                              className="input w-24"
                              placeholder="Qty"
                              value={holdQty[ev.id] ?? ""}
                              onChange={(e) => setHoldQty((m) => ({ ...m, [ev.id]: e.target.value }))}
                            />
                            <button
                              className="btn-secondary"
                              onClick={async () => {
                                const qty = Number(holdQty[ev.id] || 0);
                                if (!qty) return;
                                await axios.post(`/api/admin/events/${ev.id}/vip/hold`, null, { params: { qty, reason: "admin UI hold" } });
                                setHoldQty((m) => ({ ...m, [ev.id]: "" }));
                                refreshVipSummary(ev.id);
                              }}
                            >
                              Hold VIP
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min={1}
                              className="input w-24"
                              placeholder="Qty"
                              value={relQty[ev.id] ?? ""}
                              onChange={(e) => setRelQty((m) => ({ ...m, [ev.id]: e.target.value }))}
                            />
                            <button
                              className="btn-secondary"
                              onClick={async () => {
                                const qty = Number(relQty[ev.id] || 0);
                                if (!qty) return;
                                await axios.post(`/api/admin/events/${ev.id}/vip/release`, null, { params: { qty, reason: "admin UI release" } });
                                setRelQty((m) => ({ ...m, [ev.id]: "" }));
                                refreshVipSummary(ev.id);
                              }}
                            >
                              Release VIP
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Edit Prices */}
                    {editingPrices === ev.id && (
                      <div className="mt-4">
                        <EditPricesForm
                          event={ev}
                          onSave={handlePriceUpdate}
                          onCancel={() => setEditingPrices(null)}
                        />
                      </div>
                    )}

                    {/* Stats */}
                    {selectedEvent === ev.id && stats && (
                      <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                        <h3 className="text-lg font-semibold text-white">Event Stats</h3>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <Metric label="Total Tickets Sold" value={stats.totalTicketsSold ?? 0} />
                          <Metric
                            label="Standard Tickets Sold"
                            value={stats.standardTicketsSold ?? 0}
                          />
                          <Metric label="VIP Tickets Sold" value={stats.vipTicketsSold ?? 0} />
                          <Metric
                            label="Total Revenue"
                            value={`£${Number(stats.totalRevenue || 0).toFixed(2)}`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

function EditPricesForm({ event, onSave, onCancel }) {
  const [standardPrice, setStandardPrice] = useState(
    event.standardPrice ?? event.standardTicketPrice ?? 0
  );
  const [vipPrice, setVipPrice] = useState(event.vipPrice ?? event.vipTicketPrice ?? 0);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <h4 className="font-medium text-white">Edit Ticket Prices</h4>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
            Standard Price (£)
          </span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={standardPrice}
            onChange={(e) => setStandardPrice(e.target.value)}
            className="input w-full"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
            VIP Price (£)
          </span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={vipPrice}
            onChange={(e) => setVipPrice(e.target.value)}
            className="input w-full"
          />
        </label>
      </div>
      <div className="mt-3 flex gap-2">
        <button className="btn-primary" onClick={() => onSave(event.id, standardPrice, vipPrice)}>
          Save
        </button>
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-0.5 font-medium text-white">{value}</div>
    </div>
  );
}
